import { Button, Divider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import {
	advisoriesLoadingSelector,
	advisoriesObjectSelector,
	setPlaceDetails
} from 'slice-reducers/locationAndAdvisoriesSlice';
import {
	geolocationErrorSelector,
	userCoordinatesSelector,
	userLocationLoadingSelector
} from 'slice-reducers/appSlice';
import UserLocationLoading from 'components/rightPanel/searchAndAdvisories/UserLocationLoading';
import GeolocationErrorMessage from 'components/rightPanel/searchAndAdvisories/GeolocationErrorMessage';
import {
	addToRecentSearchesLS,
	getDetailPaneSizeFromLS,
	getRecentSearchesFromLS
} from 'actions/localStorage';
import { mapPinCoordinatesSelector } from 'slice-reducers/mapSlice';
import AdvisoriesList from 'components/rightPanel/searchAndAdvisories/advisories/AdvisoriesList';
import { useLocation, useNavigate } from 'react-router-dom';
import PlaceDetails from 'components/rightPanel/searchAndAdvisories/PlaceDetails';
import AirspaceStatus from 'components/rightPanel/searchAndAdvisories/AirspaceStatus';
import { $primary, $white } from 'styles/colors';
import { ReactComponent as NotifyIcon } from 'assets/icons/notify.svg';
import { aboutPilotInstituteLink, aboutLink, termsLink } from 'constants/externalLinks';
import { logEvent } from 'actions/firebase';
import {
	MENU_PILOT_INSTITUTE,
	MENU_KH,
	MENU_TERMS,
	NOTIFY,
	PIN_DROP_SEARCH
} from 'constants/firebase';
import { addCoordinatesToUrl, getQueryParamsFromLocation, setUrl } from 'actions/url';
import LocationSearchBox from 'components/rightPanel/searchAndAdvisories/LocationSearchBox';

const styles = {
	detailPanelContainer: {
		width: '100%',
		height: '100%'
	},
	belowSearchBar: {
		height: 'calc(100% - 57px)'
	},
	footer: {
		height: 50,
		position: 'absolute',
		width: '100%',
		bottom: 0,
		backgroundColor: $white,
		zIndex: 100
	},
	divider: {
		width: '100%'
	},
	footerLinks: {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-around',
		alignItems: 'center',
		height: '100%'
	},
	link: {
		cursor: 'pointer',
		color: $primary,
		fontSize: 16
	},
	buttonContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: 20,
		paddingBottom: 10
	},
	button: {
		textTransform: 'none',
		fontSize: 15,
		borderRadius: 0
	}
};

const SearchAndAdvisoriesContainer = () => {
	const useStyles = makeStyles(styles);
	const classes = useStyles();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const userLocationLoading = useSelector(userLocationLoadingSelector);
	const geolocationError = useSelector(geolocationErrorSelector);
	const userCoordinates = useSelector(userCoordinatesSelector);
	const advisoriesObject = useSelector(advisoriesObjectSelector);
	const advisoriesLoading = useSelector(advisoriesLoadingSelector);
	const mapPinCoordinates = useSelector(mapPinCoordinatesSelector);
	const recentSearches = getRecentSearchesFromLS();
	const queryParams = getQueryParamsFromLocation(location);

	const detailPaneSize = getDetailPaneSizeFromLS();

	const onSelectLocation = value => {
		if (value.optionType !== 'userLocation') {
			addToRecentSearchesLS(value);
		}
		dispatch(setPlaceDetails(value));
		const lat = value.geometry.coordinates[1];
		const long = value.geometry.coordinates[0];
		logEvent(PIN_DROP_SEARCH);
		addCoordinatesToUrl({ lat, long, navigate, pathname: '/' });
	};

	//Show loader if geolocation is in progress and there is not already a lat/long set in url OR if advisories are loading
	const showLoader =
		(userLocationLoading && !geolocationError && !(queryParams.lat || queryParams.long)) ||
		advisoriesLoading;

	//Show geolocation error if there was an error finding the user's location or if there is no location selected
	const showGeolocationError =
		geolocationError && !(mapPinCoordinates.lat || mapPinCoordinates.long);

	const handleClickNotify = () => {
		logEvent(NOTIFY);
		setUrl({ queryObject: queryParams, navigate, path: '/notify' });
	};

	const handleClickAboutPilotInstitute = () => {
		logEvent(MENU_PILOT_INSTITUTE);
		window.open(aboutPilotInstituteLink);
	};

	const handleClickAboutKH = () => {
		logEvent(MENU_KH);
		window.open(aboutLink);
	};

	const handleClickTerms = () => {
		logEvent(MENU_TERMS);
		window.open(termsLink);
	};

	return (
		<div className={classes.detailPanelContainer}>
			<LocationSearchBox
				onSelect={onSelectLocation}
				inputPlaceholder={'Search for a location'}
				recentSearches={recentSearches}
				currentLocation={userCoordinates && [userCoordinates.long, userCoordinates.lat]}
				height={57}
				bottomBorder
				width={detailPaneSize}
			/>
			<div className={classes.belowSearchBar}>
				{advisoriesObject && !advisoriesLoading && (
					<>
						<PlaceDetails />
						<AirspaceStatus advisoriesObject={advisoriesObject} />
						<div className={classes.buttonContainer}>
							<Button
								variant='contained'
								color='primary'
								size='large'
								className={classes.button}
								startIcon={<NotifyIcon />}
								onClick={handleClickNotify}
							>
								Notify & Fly
							</Button>
						</div>
						<AdvisoriesList advisories={advisoriesObject.advisories} />
					</>
				)}
				{showLoader && <UserLocationLoading />}
				{showGeolocationError && <GeolocationErrorMessage />}
				<div className={classes.footer}>
					<Divider className={classes.divider} />
					<div className={classes.footerLinks}>
						<div className={classes.link} onClick={handleClickAboutPilotInstitute}>
							About Pilot Institute
						</div>
						<div className={classes.link} onClick={handleClickAboutKH}>
							About Aloft
						</div>
						<div className={classes.link} onClick={handleClickTerms}>
							Terms of Use
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
export default SearchAndAdvisoriesContainer;
